/**
 * 컴포넌트별 상세 옵션 에디터
 */
import type { ComponentConfig, ComponentOptions } from '@/types/configuration'
import { DiaryQuizSetSelector } from './DiaryQuizSetSelector'

interface ComponentOptionsEditorProps {
  component: ComponentConfig
  onUpdate: (component: ComponentConfig) => void
}

export const ComponentOptionsEditor: React.FC<ComponentOptionsEditorProps> = ({
  component,
  onUpdate
}) => {
  const options = component.options || {}

  const updateOption = <K extends keyof ComponentOptions>(
    key: K,
    value: ComponentOptions[K]
  ) => {
    onUpdate({
      ...component,
      options: {
        ...options,
        [key]: value
      }
    })
  }

  // 비디오 옵션
  if (component.type === 'video') {
    return (
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">비디오 설정</h4>

        <div className="grid grid-cols-2 gap-4">
          {/* 스킵 허용 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.allowSkip ?? false}
              onChange={(e) => updateOption('allowSkip', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">스킵 허용</span>
          </label>

          {/* 첫 시청 후 스킵 허용 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.allowSkipAfterFirstWatch ?? true}
              onChange={(e) => updateOption('allowSkipAfterFirstWatch', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">첫 시청 후 스킵 허용</span>
          </label>

          {/* 배속 재생 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.allowPlaybackSpeed ?? true}
              onChange={(e) => updateOption('allowPlaybackSpeed', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">배속 재생 허용</span>
          </label>
        </div>

        {/* 최소 시청률 */}
        <div>
          <label className="block text-sm text-gray-700 mb-1">최소 시청률 (%)</label>
          <input
            type="number"
            min={0}
            max={100}
            value={options.requiredWatchPercent ?? 90}
            onChange={(e) => updateOption('requiredWatchPercent', parseInt(e.target.value) || 0)}
            className="w-24 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>
    )
  }

  // 퀴즈 옵션
  if (component.type === 'quiz') {
    return (
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">퀴즈 설정</h4>

        <div className="grid grid-cols-2 gap-4">
          {/* 즉시 정답 표시 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.showAnswerImmediately ?? false}
              onChange={(e) => updateOption('showAnswerImmediately', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">문제별 즉시 정답 표시</span>
          </label>

          {/* 제출 후 정답 표시 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.showAnswerAfterSubmit ?? true}
              onChange={(e) => updateOption('showAnswerAfterSubmit', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">제출 후 정답 표시</span>
          </label>

          {/* 재시도 허용 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.allowRetry ?? true}
              onChange={(e) => updateOption('allowRetry', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">재시도 허용</span>
          </label>

          {/* 틀린 문제만 재시도 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.retryIncorrectOnly ?? false}
              onChange={(e) => updateOption('retryIncorrectOnly', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">틀린 문제만 재시도</span>
          </label>

          {/* 문제 순서 랜덤 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.shuffleQuestions ?? false}
              onChange={(e) => updateOption('shuffleQuestions', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">문제 순서 랜덤</span>
          </label>

          {/* 선택지 순서 랜덤 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.shuffleOptions ?? false}
              onChange={(e) => updateOption('shuffleOptions', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">선택지 순서 랜덤</span>
          </label>

          {/* 힌트 표시 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.showHint ?? true}
              onChange={(e) => updateOption('showHint', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">힌트 표시</span>
          </label>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {/* 최대 시도 횟수 */}
          <div>
            <label className="block text-sm text-gray-700 mb-1">최대 시도 횟수</label>
            <input
              type="number"
              min={0}
              value={options.maxAttempts ?? 0}
              onChange={(e) => updateOption('maxAttempts', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-xs text-gray-500">0 = 무제한</span>
          </div>

          {/* 통과 점수 */}
          <div>
            <label className="block text-sm text-gray-700 mb-1">통과 점수 (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              value={options.passingScore ?? 70}
              onChange={(e) => updateOption('passingScore', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* 시간 제한 */}
          <div>
            <label className="block text-sm text-gray-700 mb-1">시간 제한 (분)</label>
            <input
              type="number"
              min={0}
              value={options.timeLimitMinutes ?? 0}
              onChange={(e) => updateOption('timeLimitMinutes', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-xs text-gray-500">0 = 제한 없음</span>
          </div>
        </div>

        {/* 문제 개수 설정 */}
        <div className="border-t pt-4 mt-4">
          <h5 className="text-sm font-medium text-gray-700 mb-3">문제 개수 설정</h5>
          <div className="grid grid-cols-2 gap-4">
            {/* 바람직한 문제 개수 */}
            <div>
              <label className="block text-sm text-gray-700 mb-1">바람직한 문제 개수</label>
              <input
                type="number"
                min={0}
                value={options.questionCount ?? 0}
                onChange={(e) => updateOption('questionCount', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="text-xs text-gray-500">0 = 전체</span>
            </div>

            {/* 문제 부족 시 전략 */}
            <div>
              <label className="block text-sm text-gray-700 mb-1">문제 부족 시</label>
              <select
                value={options.questionShortageStrategy ?? 'available_only'}
                onChange={(e) => updateOption('questionShortageStrategy', e.target.value as 'available_only' | 'fill_from_completed')}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="available_only">있는 만큼만</option>
                <option value="fill_from_completed">완료한 레슨에서 채우기</option>
              </select>
              <span className="text-xs text-gray-500">
                {options.questionShortageStrategy === 'fill_from_completed'
                  ? '최근 완료한 레슨의 post-quiz에서 가져옴'
                  : '현재 레슨의 문제만 사용'}
              </span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // 과제 옵션
  if (component.type === 'assignment') {
    return (
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">과제 설정</h4>

        {/* 생각 정리하기 퀴즈 세트 선택 */}
        <DiaryQuizSetSelector
          value={options.diaryQuizSetId}
          onChange={(setId) => updateOption('diaryQuizSetId', setId)}
        />

        <div className="grid grid-cols-2 gap-4">
          {/* 늦은 제출 허용 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={options.allowLateSubmission ?? false}
              onChange={(e) => updateOption('allowLateSubmission', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">늦은 제출 허용</span>
          </label>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* 늦은 제출 페널티 */}
          <div>
            <label className="block text-sm text-gray-700 mb-1">늦은 제출 페널티 (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              value={options.latePenaltyPercent ?? 0}
              onChange={(e) => updateOption('latePenaltyPercent', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* 최대 파일 크기 */}
          <div>
            <label className="block text-sm text-gray-700 mb-1">최대 파일 크기 (MB)</label>
            <input
              type="number"
              min={1}
              value={options.maxFileSizeMb ?? 10}
              onChange={(e) => updateOption('maxFileSizeMb', parseInt(e.target.value) || 10)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>
    )
  }

  // 기타 컴포넌트 (옵션 없음)
  return (
    <div className="text-sm text-gray-500">
      이 컴포넌트에는 추가 설정이 없습니다.
    </div>
  )
}
